// A device card for the room grid that shows its operable controls INLINE
// (no need to open a side panel for simple actions).
//
// Touch switches (1 / 2 / 4 gang) render one button per output plus All On /
// All Off. Interaction model (mirrors the mobile app):
//   - a 3-dots menu on the card       â†’ device-level common settings
//   - RIGHT-CLICK an individual button â†’ that output's schedule / details
// Every other device type falls back to the icon tile that opens the full
// control panel. The switch/case is the single place to extend inline controls
// to more device types (fan speed, curtain, â€¦).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Box, Typography, Button, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import { publishCommand } from '../../../core/utils/deviceController';
import { deviceTypeKey } from '../../../core/devices/deviceMeta';
import { getOutputs, getDeviceStatus } from '../../../core/api/api';
import FanDial from './FanDial';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';
import DoorControlCard from './DoorControlCard';
import LightControlCard from './LightControlCard';
import CurtainControlCard from './CurtainControlCard';
import GenericControlCard from './GenericControlCard';
import PhaseControlCard from './PhaseControlCard';
import { panelGridSx } from '../../../core/devices/buttonLayout';

function parseStates(status, gangs) {
  if (!status) return Array(gangs).fill(false);
  if (gangs === 1) {
    const parts = status.split('-');
    return [parts[parts.length - 1] !== '0'];
  }
  const parts = status.split(',');
  return Array.from({ length: gangs }, (_, i) => parts[i] === '1');
}

function TouchControlCard({ device, gangs, subtitle }) {
  const { userId } = useApp();
  const navigate = useNavigate();
  const uno = device.UniqueNumber;
  const [outputs, setOutputs] = useState([]);
  // Seed the live status from the device row we already loaded (the room-device
  // list includes `Status`), so the card reflects the real state immediately â€”
  // even if the extra getDeviceStatus fetch below is slow or fails. Live MQTT
  // messages then update it through the hook's own listener.
  const [status, setStatus] = useDeviceStatus(uno, parseStatus(device.Status || device.DeviceStatus || ''));
  const [states, setStates] = useState(() => Array(gangs).fill(false));

  const [ctx, setCtx] = useState(null);                   // per-button right-click menu

  // Fetch this device's output names + last-known status so the buttons are
  // labelled and reflect current state before any live MQTT arrives. The table
  // value is applied ONLY if no live status has arrived yet (`cur || db`), so a
  // fresh MQTT update that lands during the fetch is never overwritten.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [out, st] = await Promise.all([
        getOutputs(uno).catch(() => null),
        getDeviceStatus(uno).catch(() => null),
      ]);
      if (!alive) return;
      setOutputs(out && Array.isArray(out.data) ? out.data : []);
      const row = st && Array.isArray(st.data) ? st.data[0] : null;
      if (row && row.Status) {
        const db = parseStatus(row.Status);
        if (db) setStatus((cur) => cur || db);
      }
    })();
    return () => { alive = false; };
  }, [uno, setStatus]);

  useEffect(() => { if (status) setStates(parseStates(status, gangs)); }, [status, gangs]);

  const toggle = (i) => {
    const next = !states[i];
    const updated = [...states];
    updated[i] = next;
    setStates(updated);
    publishCommand(uno, `${i + 1}-${next ? 1 : 0}`, userId);
  };

  const setAll = (on) => {
    setStates(Array(gangs).fill(on));
    publishCommand(uno, on ? 'allon' : 'alloff', userId);
  };

  const outName = (i) => {
    const o = outputs.find((x) => Number(x.Output) === i + 1);
    return (o && o.OutputName) || `Switch ${i + 1}`;
  };

  const openButtonMenu = (e, i) => {
    e.preventDefault(); // suppress the browser's native context menu
    setCtx({ mouseX: e.clientX + 2, mouseY: e.clientY - 6, output: i + 1, name: outName(i) });
  };

  const goSchedule = () => {
    if (!ctx) return;
    navigate(`/schedule/${uno}/${ctx.output}`, { state: { deviceId: device.DeviceID, name: ctx.name } });
    setCtx(null);
  };

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} live={status} />
        <DeviceCardMenu device={device} />
      </Box>

      {/* Laid out as the panel is: two rows, filling DOWN each column, so a
          6-gang reads 1 3 5 / 2 4 6 exactly as it does on the wall. The grid
          flows by column, so channels are rendered in natural order and must
          NOT be permuted as well — see core/devices/buttonLayout. */}
      <Box sx={panelGridSx(gangs)}>
        {Array.from({ length: gangs }, (_, i) => {
          const on = states[i];
          return (
            <Button
              key={i}
              onClick={() => toggle(i)}
              onContextMenu={(e) => openButtonMenu(e, i)}
              variant={on ? 'contained' : 'outlined'}
              color={on ? 'success' : 'inherit'}
              startIcon={<PowerSettingsNewIcon />}
              sx={{
                justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2,
                py: 0.5, color: on ? undefined : 'text.primary',
                borderColor: on ? undefined : 'divider',
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: 'left', lineHeight: 1.1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{outName(i)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{on ? 'ON' : 'OFF'}</Typography>
              </Box>
            </Button>
          );
        })}
      </Box>

      {gangs > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Button size="small" fullWidth variant="contained" onClick={() => setAll(true)}>All On</Button>
          <Button size="small" fullWidth variant="outlined" onClick={() => setAll(false)}>All Off</Button>
        </Box>
      )}

      {/* Per-button right-click â€” that output's schedule / details. */}
      <Menu
        open={ctx !== null}
        onClose={() => setCtx(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctx ? { top: ctx.mouseY, left: ctx.mouseX } : undefined}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }}>
            {ctx?.name}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={goSchedule}>
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Schedule</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}

function FanControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;
  // Seed from the already-loaded device row so the dial shows the real speed
  // immediately (fixes the fan showing OFF when its status is e.g. `1-100`).
  const seed = parseStatus(device.Status || device.DeviceStatus || '');

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu device={device} />
      </Box>

      <FanDial device={device} size={140} initialStatus={seed} />
    </Card>
  );
}

// Every device type renders an inline control card â€” the grid never opens a
// side window.
export default function DeviceControlCard({ device, subtitle }) {
  const key = deviceTypeKey(device.DeviceID);
  switch (key) {
    case 'oneTouch': return <TouchControlCard device={device} gangs={1} subtitle={subtitle} />;
    case 'twoTouch': return <TouchControlCard device={device} gangs={2} subtitle={subtitle} />;
    case 'fourTouch': return <TouchControlCard device={device} gangs={4} subtitle={subtitle} />;
    case 'fan': return <FanControlCard device={device} subtitle={subtitle} />;
    case 'door': return <DoorControlCard device={device} subtitle={subtitle} />;
    case 'curtain': return <CurtainControlCard device={device} subtitle={subtitle} />;
    case 'rgb':
    case 'wwh':
    case 'wwrgb': return <LightControlCard device={device} subtitle={subtitle} />;
    case 'singlePhase':
    case 'threePhase': return <PhaseControlCard device={device} />;
    default: return <GenericControlCard device={device} subtitle={subtitle} />;
  }
}
